/**
 * The shared vertical time axis of the timeline (W1-15 timeline-host; ARCHITECTURE.md §7.7, DESIGN.md §4.6).
 *
 * Time flows UP: y = 0 is the top edge (later beats), y = heightPx the bottom edge (`bottomBeat`). Beats are f64
 * here (display only); `snap` turns a display beat into an exact `Beat` on the current grid with the core rational
 * arithmetic (`beatFromF64Snapped`, ties toward +infinity), so an edit never carries a rounding error:
 *   beatAtY(y) = bottomBeat + (heightPx - y) / pxPerBeat
 *   yAtBeat(b) = heightPx - (b - bottomBeat) * pxPerBeat
 * With beat snap off the grid is 1/1920 beat (the finest RPE-safe subdivision; DESIGN.md §8.2 "Alt bypasses snap").
 *
 * Also here: zoom/scroll math (zoom about an anchor, follow position, clamping), wheel delta normalisation,
 * the musical snap presets and their Alt+wheel cycling, and the tick classification the gutter and the columns
 * use for osu!-style divisor colours.
 */

import { beatFromF64Snapped } from '../../core/time/beat';
import type { MeterMap } from '../../core/time/meter';
import type { Beat } from '../../core/time/types';
import type { TimeAxis, TimelineViewConfig } from '../types';
import type { TimelineTheme } from './theme';
import type { WaveformSource } from './waveformColumn';
import { WHEEL_NOTCH_PX, wheelDeltaPx } from '../../core/util/wheel';

/** Zoom limits in CSS px per beat. */
export const MIN_PX_PER_BEAT = 2;
export const MAX_PX_PER_BEAT = 6000;
/** Finest grid: used when beat snap is off and as the upper bound of custom divisors. */
export const FINEST_DIV = 1920;
/** Musical snap presets (DESIGN.md §4.4: 1/1 ... 1/32, triplets), the order Alt+wheel walks through. */
export const SNAP_PRESETS: readonly number[] = [1, 2, 3, 4, 6, 8, 12, 16, 24, 32];
/** Furthest the view may scroll past the last beat anyone could chart (keeps snapping inside i32 beats). */
export const MAX_VIEW_BEAT = 1_000_000;
/** One wheel notch (100 normalised px) zooms by this factor. */
export const WHEEL_ZOOM_PER_NOTCH = 1.25;
/**
 * Wheel units shared with the keymap's wheel chords (core/util/wheel.ts), so a notch scrolls the same amount whether
 * the host or a wheel-bound command handles it: 40 px per line, 100 px per notch (sensitivity unit, Alt+wheel snap
 * steps).
 */
export { WHEEL_LINE_PX, WHEEL_NOTCH_PX } from '../../core/util/wheel';

export interface AxisState {
  readonly pxPerBeat: number;
  readonly bottomBeat: number;
  readonly heightPx: number;
  readonly dpr: number;
  readonly snapDiv: number;
  readonly beatSnap: boolean;
}

/**
 * Shared drawing state the host attaches to its axis, so a column view can draw the beat grid, the meter's bar
 * lines and token colours consistently with the gutter (`axisContext(axis)` inside `draw`).
 */
export interface AxisContext {
  /** Compiled editor meter (Timing.meter) for bar lines. */
  readonly meter: MeterMap;
  readonly theme: TimelineTheme;
  /** Playhead beat (f64). */
  readonly playheadBeat: number;
  /** Current view configuration (snap divisor, waveform opacity, ...). */
  readonly view: TimelineViewConfig;
  /**
   * The song's waveform while one is set (`host.setWaveform`), for the backdrop behind the lane grid
   * (`drawWaveform` with `view.waveformOpacity`); the host redraws every static layer when it changes.
   */
  readonly waveform?: WaveformSource | null;
}

/** TimeAxis plus the host's mutation and change tracking. */
export interface MutableTimeAxis extends TimeAxis {
  /** Drawing context supplied by the host (null for a bare axis). */
  readonly context: AxisContext | null;
  readonly snapDiv: number;
  readonly beatSnap: boolean;
  /** Visible length in beats (heightPx / pxPerBeat). */
  readonly span: number;
  readonly topBeat: number;
  /** Increments whenever the beat <-> y mapping or the pixel ratio changes (not on snap changes). */
  readonly version: number;
  /** Applies a partial state; returns true when anything changed. */
  update(p: Partial<AxisState>): boolean;
  state(): AxisState;
  /** Snap to an explicit divisor (Alt = 1/1920). */
  snapTo(beat: number, div: number): Beat;
}

function finiteOr(x: number, fallback: number): number {
  return Number.isFinite(x) ? x : fallback;
}

export function clampPxPerBeat(pxPerBeat: number): number {
  return Math.min(MAX_PX_PER_BEAT, Math.max(MIN_PX_PER_BEAT, finiteOr(pxPerBeat, 120)));
}

/** A snap divisor as a positive integer in 1..1920 (invalid -> 4). */
export function clampDiv(div: number): number {
  if (!Number.isFinite(div)) return 4;
  return Math.min(FINEST_DIV, Math.max(1, Math.round(div)));
}

/**
 * Keeps the view sane: at least the top edge stays at or above beat 0 (so the chart start can sit at the
 * playhead), and the bottom never passes MAX_VIEW_BEAT.
 */
export function clampBottomBeat(bottomBeat: number, heightPx: number, pxPerBeat: number): number {
  const span = heightPx > 0 ? heightPx / pxPerBeat : 0;
  return Math.min(MAX_VIEW_BEAT, Math.max(-span, finiteOr(bottomBeat, 0)));
}

/** Nearest multiple of 1/div to `beat` as an exact Beat (ties toward +infinity). */
export function snapBeat(beat: number, div: number): Beat {
  const b = Math.min(MAX_VIEW_BEAT, Math.max(-MAX_VIEW_BEAT, finiteOr(beat, 0)));
  return beatFromF64Snapped(b, clampDiv(div));
}

/** f64 value of the nearest grid position (display helpers; edits use `snapBeat`). */
export function snapBeatF64(beat: number, div: number): number {
  const d = clampDiv(div);
  return Math.floor(beat * d + 0.5) / d;
}

/** Bottom beat that keeps `anchorBeat` at `anchorY` for a given zoom. */
export function bottomForAnchor(anchorBeat: number, anchorY: number, heightPx: number, pxPerBeat: number): number {
  return anchorBeat - (heightPx - anchorY) / pxPerBeat;
}

/** Bottom beat that puts the playhead at `ratio` of the height from the bottom (follow mode). */
export function bottomForPlayhead(playheadBeat: number, ratio: number, heightPx: number, pxPerBeat: number): number {
  const r = Math.min(0.95, Math.max(0, finiteOr(ratio, 0.25)));
  return playheadBeat - (r * heightPx) / pxPerBeat;
}

/** New zoom and bottom for zooming by `factor` around the beat at y = `anchorY`. */
export function zoomAround(
  state: Pick<AxisState, 'pxPerBeat' | 'bottomBeat' | 'heightPx'>,
  factor: number,
  anchorY: number,
): { readonly pxPerBeat: number; readonly bottomBeat: number } {
  const anchorBeat = state.bottomBeat + (state.heightPx - anchorY) / state.pxPerBeat;
  const pxPerBeat = clampPxPerBeat(state.pxPerBeat * factor);
  return { pxPerBeat, bottomBeat: bottomForAnchor(anchorBeat, anchorY, state.heightPx, pxPerBeat) };
}

/** Zoom factor for a normalised vertical wheel delta (negative deltaY = wheel up / pinch out = zoom in). */
export function wheelZoomFactor(deltaYPx: number): number {
  return Math.pow(WHEEL_ZOOM_PER_NOTCH, -deltaYPx / WHEEL_NOTCH_PX);
}

/** Wheel deltas in CSS px whatever the deltaMode (0 pixel, 1 line, 2 page): core/util/wheel.ts. */
export const normalizeWheel = wheelDeltaPx;

/**
 * Beats to scroll for a normalised vertical delta. Wheel down / swipe that reports positive deltaY moves LATER in
 * time (the rhythm-editor convention: like playing, notes move down towards the playhead). `sensitivity` is
 * settings.editing.scrollSensitivity, beats per 100 px; `fast` multiplies by 4 (Shift, or Alt in the RPE preset).
 */
export function scrollBeatsForDelta(deltaYPx: number, sensitivity: number, fast: boolean): number {
  const s = Number.isFinite(sensitivity) && sensitivity > 0 ? sensitivity : 1;
  return (deltaYPx / WHEEL_NOTCH_PX) * s * (fast ? 4 : 1);
}

/** Next musical snap divisor: dir > 0 = finer (1/4 -> 1/6), dir < 0 = coarser; clamps at both ends. */
export function cycleSnapDiv(div: number, dir: number): number {
  const d = clampDiv(div);
  if (dir > 0) return SNAP_PRESETS.find((p) => p > d) ?? SNAP_PRESETS[SNAP_PRESETS.length - 1] ?? d;
  if (dir < 0) {
    for (let i = SNAP_PRESETS.length - 1; i >= 0; i--) {
      const p = SNAP_PRESETS[i];
      if (p !== undefined && p < d) return p;
    }
    return SNAP_PRESETS[0] ?? d;
  }
  return d;
}

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x;
}

/** Reduced denominator of k/div within its beat (1 = on a whole beat; 4 = an odd quarter...). */
export function tickDenominator(k: number, div: number): number {
  const d = clampDiv(div);
  const n = ((k % d) + d) % d;
  if (n === 0) return 1;
  return d / gcd(n, d);
}

/**
 * Densest subdivision to draw: the largest divisor of `div` whose ticks are at least `minSpacingPx` apart.
 * Returns 0 when even whole beats are closer than that (the caller then draws bar lines only).
 */
export function visibleSubdivision(div: number, pxPerBeat: number, minSpacingPx: number): number {
  const d = clampDiv(div);
  for (let q = d; q >= 1; q--) {
    if (d % q === 0 && pxPerBeat / q >= minSpacingPx) return q;
  }
  return 0;
}

/**
 * Calls `cb(beat, k, den)` for every tick k/div in [beat0, beat1] (ascending), where den is the reduced
 * denominator (tick colour). At most `limit` ticks (guards against absurd zoom/divisor combinations).
 */
export function forEachTick(
  beat0: number,
  beat1: number,
  div: number,
  cb: (beat: number, k: number, den: number) => void,
  limit = 20000,
): void {
  const d = clampDiv(div);
  if (!(beat1 >= beat0)) return;
  const k0 = Math.ceil(beat0 * d - 1e-9);
  const k1 = Math.floor(beat1 * d + 1e-9);
  let count = 0;
  for (let k = k0; k <= k1 && count < limit; k++, count++) cb(k / d, k, tickDenominator(k, d));
}

/** Device pixel ratio of the window (1 outside a browser). */
export function currentDpr(): number {
  const dpr = typeof devicePixelRatio === 'number' ? devicePixelRatio : 1;
  return Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
}

const STRIP_AXES = new WeakSet<TimeAxis>();

/**
 * An axis spanning a scroll-cache strip (TimelineHost): a column draws its static and content layers with it for a
 * range taller than the visible one. Views keep the live axis (overlay, hit-tests) for pointer mapping, never this.
 */
export function createStripAxis(init: Partial<AxisState>, context: AxisContext | null): MutableTimeAxis {
  const a = createTimeAxis(init, context);
  STRIP_AXES.add(a);
  return a;
}

/** True for an axis made by `createStripAxis` (not the visible range). */
export function isStripAxis(axis: TimeAxis): boolean {
  return STRIP_AXES.has(axis);
}

/** The host's drawing context of an axis passed to a column, or null for a plain TimeAxis. */
export function axisContext(axis: TimeAxis): AxisContext | null {
  return 'context' in axis ? ((axis as MutableTimeAxis).context ?? null) : null;
}

/** Creates the mutable axis the host shares with every column. */
export function createTimeAxis(init: Partial<AxisState> = {}, context: AxisContext | null = null): MutableTimeAxis {
  let s: AxisState = {
    pxPerBeat: clampPxPerBeat(init.pxPerBeat ?? 120),
    bottomBeat: finiteOr(init.bottomBeat ?? 0, 0),
    heightPx: Math.max(0, finiteOr(init.heightPx ?? 0, 0)),
    dpr: init.dpr ?? 1,
    snapDiv: clampDiv(init.snapDiv ?? 4),
    beatSnap: init.beatSnap ?? true,
  };
  let range: readonly [number, number] = [s.bottomBeat, s.bottomBeat + s.heightPx / s.pxPerBeat];
  let version = 0;

  const axis: MutableTimeAxis = {
    context,
    get pxPerBeat() {
      return s.pxPerBeat;
    },
    get bottomBeat() {
      return s.bottomBeat;
    },
    get heightPx() {
      return s.heightPx;
    },
    get dpr() {
      return s.dpr;
    },
    get snapDiv() {
      return s.snapDiv;
    },
    get beatSnap() {
      return s.beatSnap;
    },
    get span() {
      return s.heightPx / s.pxPerBeat;
    },
    get topBeat() {
      return s.bottomBeat + s.heightPx / s.pxPerBeat;
    },
    get range() {
      return range;
    },
    get version() {
      return version;
    },
    beatAtY: (y) => s.bottomBeat + (s.heightPx - y) / s.pxPerBeat,
    yAtBeat: (beat) => s.heightPx - (beat - s.bottomBeat) * s.pxPerBeat,
    snap: (beat) => snapBeat(beat, s.beatSnap ? s.snapDiv : FINEST_DIV),
    snapTo: (beat, div) => snapBeat(beat, div),
    update(p) {
      const next: AxisState = {
        pxPerBeat: p.pxPerBeat === undefined ? s.pxPerBeat : clampPxPerBeat(p.pxPerBeat),
        bottomBeat: p.bottomBeat === undefined ? s.bottomBeat : finiteOr(p.bottomBeat, s.bottomBeat),
        heightPx: p.heightPx === undefined ? s.heightPx : Math.max(0, finiteOr(p.heightPx, 0)),
        dpr: p.dpr === undefined ? s.dpr : p.dpr > 0 ? p.dpr : 1,
        snapDiv: p.snapDiv === undefined ? s.snapDiv : clampDiv(p.snapDiv),
        beatSnap: p.beatSnap ?? s.beatSnap,
      };
      const mapping =
        next.pxPerBeat !== s.pxPerBeat ||
        next.bottomBeat !== s.bottomBeat ||
        next.heightPx !== s.heightPx ||
        next.dpr !== s.dpr;
      const snapChanged = next.snapDiv !== s.snapDiv || next.beatSnap !== s.beatSnap;
      s = next;
      if (mapping) {
        version++;
        range = [s.bottomBeat, s.bottomBeat + s.heightPx / s.pxPerBeat];
      }
      return mapping || snapChanged;
    },
    state: () => s,
  };
  return axis;
}
